import React, { useEffect, useMemo, useState } from 'react';

import Chart from './chart';
import moment from 'moment';
import { getNewsStatsTimeline } from '@/pages/opinion/services/news';
import {
  addRateValue,
  addWithoutSingleData,
  retainDecimals,
} from '@/pages/opinion/utils';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import CardCommon from '@/pages/opinion/components/CardCommon';
import './index.scss';
import { useIntl } from 'umi';
import { reviewsFormat, sentimentFormat } from '@op/utils/value';
import { newsDataSourceIconMap } from '@op/constants/news';
import BubbleCommon, {
  newsEngagementTips,
  newsTotalArticlesTips,
  newsUVPMTips,
} from '@op/components/BubbleCommon';
import DataSourceList from '../DataSourceList';

export default function NewsVolumeAndSentiment({
  params,
  showIEGGPr,
  dataSourceList
}: {
  params: any;
  showIEGGPr: boolean;
  dataSourceList: string[];
}) {
  const { formatMessage } = useIntl();

  const [originData, setOriginData] = useState<any>({});
  const [list, setList] = useState<any>([]);
  const [mention, setMention] = useState<any>([]);
  const [oldMention, setOldMention] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setApiError(false);
    getNewsStatsTimeline({
      ...params,
      types: ['total', 'total_link', 'slice'],
    })
      .then((data: any) => {
        let {
          timeline_of_end_time,
          timeline_of_start_time,
          timeline_of_mentions: list,
          mentions,
          old_mentions: oldMentions,
        } = data || {};
        list = addWithoutSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          params.date_type,
          list,
          {
            avg_sentiment: 0,
            count: 0,
            negative_mentions: 0,
            neutral_mentions: 0,
            positive_mentions: 0,
          },
        );
        const dataList = addRateValue(
          list || [],
          ['negative_mentions', 'neutral_mentions', 'positive_mentions'],
          'count',
          (item: any) => ({
            sentiment_avg: retainDecimals(item.avg_sentiment),
          }),
        );
        setList(dataList);
        setMention(mentions[0]);
        setOldMention(oldMentions[0]);

        setOriginData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  const percentage = (oldValue: number, newValue: number) => {
    if (newValue === 0) return 0;
    if (oldValue === 0) return 100;
    return ((newValue - oldValue) / oldValue) * 100;
  };

  const cardList = useMemo(() => {
    const list = [
      {
        title: 'Total Articles',
        key: 'count',
        tips: newsTotalArticlesTips,
        format: (value: number) => reviewsFormat(value, 2, '0'),
      },
      {
        title: 'Potential Reach',
        key: 'uvpm',
        icon: newsDataSourceIconMap['IEGG PR'],
        tips: newsUVPMTips,
      },
      {
        title: 'Engagement',
        key: 'engagement',
        icon: newsDataSourceIconMap.DataBrain,
        tips: newsEngagementTips,
        format: (value: number) => reviewsFormat(value, 2, '0'),
      },
      { title: 'Sentiment', key: 'avg_sentiment', format: sentimentFormat },
    ];

    if (!showIEGGPr) {
      return list.filter((val) => val.key !== 'uvpm');
    }

    return list;
  }, [showIEGGPr]);

  return (
    <div
      className={`dbo-news-volume ${showIEGGPr ? '' : 'dbo-news-volume-flex'}`}
    >
      <div className="dbo-news-volume-left">
        {cardList.map(({ title, key, icon, tips, format = reviewsFormat }) => {
          const oldValue = oldMention[key];
          const newValue = mention[key];

          return (
            <CardCommon
              className="dbo-card-news-item"
              key={title}
              title={
                <>
                  <BubbleCommon placement="top" tipsMap={tips} showIcon={false}>
                    {icon && <img src={icon} />}
                    {formatMessage({ id: title })}
                  </BubbleCommon>
                </>
              }
            >
              <div className='dbo-card-news-item-content'>
                <div className='dbo-card-news-item-details'>
                  <div className="dbo-card-news-item-value">
                    {format(newValue)}
                  </div>
                  <div className="dbo-card-news-item-change">
                    <span className="dbo-card-news-item-text">
                      {formatMessage({ id: 'Change' })}
                    </span>
                    <RingRatioBubble
                      oldTime={[
                        originData.link_of_start_time,
                        originData.link_of_end_time,
                      ]}
                      newTime={[params.start_time, params.end_time]}
                      oldValue={oldValue}
                      newValue={newValue}
                      formateValue={format}
                      formateDate={(value) => {
                        if (params.date_type == 'hourly') {
                          return moment(value).format('YYYY-MM-DD HH:mm');
                        } else {
                          return moment(value).format('YYYY-MM-DD');
                        }
                      }}
                      title={`${formatMessage({ id: title })} ${formatMessage({
                        id: 'Change%',
                      })}`}
                    >
                      <PercentageItem
                        percentage={percentage(oldValue, newValue)}
                      ></PercentageItem>
                    </RingRatioBubble>
                  </div>
                </div>
                {
                  key === 'count' && showIEGGPr &&
                  (
                    <DataSourceList dataSourceList={mention?.mentions_by_data_source} total={mention[key]} defaultDataSourceList={dataSourceList} />
                  )
                }
              </div>
            </CardCommon>
          );
        })}
      </div>
      <div className="dbo-news-volume-right">
        <CardCommon
          bodyStyle={{ height: 400 }}
          loadingHeight={400}
          title={formatMessage({ id: 'News Mentions & Sentiment' })}
          loading={loading}
        >
          <Chart mentionsList={list} dateType={params.date_type}></Chart>
        </CardCommon>
      </div>
    </div>
  );
}
